import { FONT_SIZE } from "../../../../theme/tokens";

/** One tick on a `SystemLinearMeter`'s value axis. */
export type SystemLinearMeterScaleMark = {
	/** Position on the value scale (clamped to `[0, max]`). */
	value: number;
	/** Text under the tick. Omit for an unlabelled hairline. */
	label?: string;
	/** Tick + label color. Falls back to the meter's `scaleTickColor` / `scaleLabelColor`. */
	color?: string;
};

/** A label that survived collision resolution, with its resolved left edge in px. */
export type PlacedScaleLabel = {
	mark: SystemLinearMeterScaleMark;
	leftPx: number;
};

// Label widths are ESTIMATED, not measured: axis labels are short tabular numbers at a known
// size, so a per-label DOM measure would buy exactness at the cost of a layout pass per resize.
const ROOT_FONT_PX = 16;
const LABEL_FONT_PX = Number.parseFloat(FONT_SIZE.LABEL_XXS) * ROOT_FONT_PX;
/** Tabular digit advance as a fraction of the font size — measured off the rendered labels. */
const CHAR_ADVANCE_RATIO = 0.65;
/** Clear space demanded between two neighbouring labels before they count as colliding. */
const LABEL_BREATHING_PX = 6;

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

const estimateLabelWidthPx = (label: string): number => label.length * LABEL_FONT_PX * CHAR_ADVANCE_RATIO;

/**
 * Greedy placement for a meter's axis labels. Marks are placed in ARRAY ORDER, which the caller
 * sets by importance — a label that would overlap an already-placed one is dropped (its tick
 * still draws), so a crowded axis sheds its least-important numbers instead of rendering them on
 * top of each other. Every label is clamped inside the track, so end labels pin flush to the
 * edges rather than overflowing the box.
 */
export const placeScaleLabels = (marks: readonly SystemLinearMeterScaleMark[], max: number, trackWidthPx: number): PlacedScaleLabel[] => {
	if (max <= 0 || trackWidthPx <= 0) return [];
	const placed: PlacedScaleLabel[] = [];
	const takenSpans: Array<[number, number]> = [];
	for (const mark of marks) {
		if (!mark.label) continue;
		const width = estimateLabelWidthPx(mark.label);
		const center = (clamp(mark.value, 0, max) / max) * trackWidthPx;
		const leftPx = clamp(center - width / 2, 0, Math.max(0, trackWidthPx - width));
		const span: [number, number] = [leftPx - LABEL_BREATHING_PX / 2, leftPx + width + LABEL_BREATHING_PX / 2];
		if (takenSpans.some(([lo, hi]) => span[0] < hi && span[1] > lo)) continue;
		takenSpans.push(span);
		placed.push({ mark, leftPx });
	}
	return placed;
};
